<script setup lang="ts">
import type { BreadcrumbItem } from "@nuxt/ui";
import type { Map } from "mapbox-gl";
import { getStructureList, getStructureGeo } from "@carnet/api/backoffice";
import type { GetStructureGeoData, GetStructureListData, StructureResource } from "@carnet/api/backoffice";
import type { UrlFilterValues } from "@/composables/useUrlFilters";
import { formatBboxCoord } from "@carnet/ui/utils/geo";
import { locationFilterKey } from "@carnet/ui/utils/location";

definePageMeta({
  name: "structures",
  layout: "app",
});

const { t } = useI18n();
const localePath = useLocalePath();
const { loggedIn } = useUserSession();

const { filter: locationFilter } = useUrlLocationFilter();

const { multi } = useUrlFilterValues();
const structureTypeCodes = multi("structureTypes");
const structureDisciplineCodes = multi("structureDisciplines");
const structureServiceCodes = multi("structureServices");

const filterCount = computed(
  () =>
    [structureTypeCodes, structureDisciplineCodes, structureServiceCodes].filter((r) => r.value.length).length +
    (locationFilter.value.location ? 1 : 0)
);

const { title, breadcrumbLabel } = usePageTitle({ base: () => t("structures.title"), filterCount });

useSeoMeta({
  title: () => title.value,
  description: () => t("seo.structures.description"),
});

const breadcrumb = computed<BreadcrumbItem[]>(() => [
  { label: t("home.title"), to: localePath({ name: "index" }) },
  { label: breadcrumbLabel.value },
]);

const filterValues = ref<UrlFilterValues>({});

const mapRef = useTemplateRef<{ mapboxMap: Map }>("mapRef");
const layoutRef = useTemplateRef<{ sheetScrollEl: HTMLElement | null; closeSheet: () => void }>("layoutRef");
const isMobile = useIsMobile();

const { bbox, total, pageStates, firstPageStatus, totalPages, generation, registerContainer, setTopRef, loadPage, retry } =
  useGeoListSearch<StructureResource>({
    getMap: () => mapRef.value?.mapboxMap,
    getRoot: () => (isMobile.value ? (layoutRef.value?.sheetScrollEl ?? null) : null),
    resetWhen: [structureTypeCodes, structureDisciplineCodes, structureServiceCodes],
    fetchPage: async ({ page, signal, bbox }) => {
      const res = await getStructureList({
        composable: "$fetch",
        signal,
        path: { locale: appLocale() },
        headers: { Accept: "application/json" },
        query: {
          page,
          sort: "center_asc",
          "bbox[nw][latitude]": formatBboxCoord(bbox.nwLat.value),
          "bbox[nw][longitude]": formatBboxCoord(bbox.nwLng.value),
          "bbox[se][latitude]": formatBboxCoord(bbox.seLat.value),
          "bbox[se][longitude]": formatBboxCoord(bbox.seLng.value),
          "types[]": structureTypeCodes.value.length ? structureTypeCodes.value : undefined,
          "disciplines[]": structureDisciplineCodes.value.length ? structureDisciplineCodes.value : undefined,
          "services[]": structureServiceCodes.value.length ? structureServiceCodes.value : undefined,
        } as NonNullable<GetStructureListData["query"]>,
      });
      return unwrapList<StructureResource>(res);
    },
  });

const GEO_CACHE_KEY = "structures-geo:default:v1";
const geoCacheKey = computed(() =>
  structureTypeCodes.value.length || structureDisciplineCodes.value.length || structureServiceCodes.value.length ? undefined : GEO_CACHE_KEY
);

const { data: geoData } = await getStructureGeo({
  composable: "useLazyFetch",
  key: "structures-geo",
  path: { locale: appLocale() },
  headers: { Accept: "application/json" },
  query: computed(
    () =>
      ({
        "types[]": structureTypeCodes.value.length ? structureTypeCodes.value : undefined,
        "disciplines[]": structureDisciplineCodes.value.length ? structureDisciplineCodes.value : undefined,
        "services[]": structureServiceCodes.value.length ? structureServiceCodes.value : undefined,
      }) as NonNullable<GetStructureGeoData["query"]>
  ),
});

const { geoMarkers } = useGeoSource({ data: geoData, cacheKey: geoCacheKey });

const { selectedId: selectedStructureId, onMarkerClick } = useMapSelection("structureId");

const locationKey = computed(() => locationFilterKey(locationFilter.value));
useAppDeselectOnChange(selectedStructureId, [structureTypeCodes, structureDisciplineCodes, structureServiceCodes, locationKey]);

const { focusOnLocation } = useMapLocationFocus(() => mapRef.value?.mapboxMap, locationFilter);

function onMapInit() {
  bbox.update();
  focusOnLocation(false);
}

function onShowStructureOnMap(structure: StructureResource) {
  selectedStructureId.value = String(structure.id);
  layoutRef.value?.closeSheet();
}

function onSelectStructure(structure: StructureResource) {
  const map = mapRef.value?.mapboxMap;
  if (!map) return;
  const feature = geoMarkers.value.features.find((f) => f.properties?.id === structure.id);
  if (!feature || feature.geometry.type !== "Point") return;
  const [lng, lat] = feature.geometry.coordinates as [number, number];
  map.flyTo({ center: [lng, lat], zoom: 11, animate: true, duration: 800 });
  selectedStructureId.value = String(structure.id);
}

useMapInitialFocus(() => mapRef.value?.mapboxMap, geoMarkers, selectedStructureId.value, { zoom: 11 });
</script>

<template>
  <WebMapListPage
    type="structures"
    ref="layoutRef"
    :breadcrumb="breadcrumb"
    :total="total"
    :bottom-padding="16"
    class="grow"
  >
    <template #search>
      <WebLocationSearch
        v-model:filter="locationFilter"
        context="structures"
        :placeholder="t('structures.search.placeholder')"
        class="pb-4"
        @select:structure="onSelectStructure"
      />

      <WebFiltersStructures v-model:applied="filterValues" />
    </template>

    <template #aside>
      <WebInfiniteList
        :page-states="pageStates"
        :first-page-status="firstPageStatus"
        :total="total"
        :total-pages="totalPages"
        :generation="generation"
        :register-container="registerContainer"
        :set-top-ref="setTopRef"
        @load="loadPage"
        @retry="retry"
      >
        <template #count>{{ t("structures.count", total) }}</template>
        <template #skeleton>
          <AppTileStructureSkeleton opens-on-map class="py-6 px-4 sm:px-10" />
        </template>
        <template #default="{ item }">
          <AppTileStructure :structure="item" opens-on-map class="py-6 px-4 sm:px-10" @select="() => onShowStructureOnMap(item)" />
        </template>
      </WebInfiniteList>
    </template>

    <template #map="{ isMobile: isMapMobile, openSheet, triggerLabel, bottomPadding: mapBottomPadding }">
      <ClientOnly>
        <LazyAppMap
          ref="mapRef"
          :markers="geoMarkers"
          :active-feature-id="selectedStructureId"
          :controls-dimmed="isMapMobile && selectedStructureId != null"
          mode="cluster"
          class="size-full"
          @init="onMapInit"
          @move-end="bbox.update"
          @marker-click="onMarkerClick"
          :bottom-padding="mapBottomPadding"
        >
          <WebMapPopupStructure v-if="selectedStructureId && !isMapMobile" :structure-id="selectedStructureId" @close="selectedStructureId = undefined" />
          <template v-if="isMapMobile" #bottom>
            <AppMobileFloatingMessage v-if="firstPageStatus === 'success' && total === 0">{{ t("ui.zone.empty") }}</AppMobileFloatingMessage>
            <AppMobileFloatingButton v-else :label="triggerLabel ?? ''" icon="icon:chevronDown" @click="openSheet" />
          </template>
          <template v-if="isMapMobile" #pinned>
            <WebMapPopupStructure v-if="selectedStructureId" :structure-id="selectedStructureId" @close="selectedStructureId = undefined" />
          </template>
        </LazyAppMap>
      </ClientOnly>
    </template>
  </WebMapListPage>
</template>
